import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from './Icons';
import { useTheme } from './ThemeContext';
import { useDateFormat } from './DateFormatContext';
import { useGuidance } from './GuidanceContext';
import { apiFetch, readApiError } from './api';
import { FIELD_OPTION_GROUP_LABELS, notifyFieldOptionsChanged } from './fieldOptions';
import ErrorScreen from './ErrorScreen';
import QRCodeSheet from './QRCodeSheet';
import { copyToClipboard } from './clipboard';

// ─── Section Card ──────────────────────────────────────────────────────────

function SectionCard({ title, children }) {
  return (
    <div className="stats-section-card">
      <h2 className="stats-section-title">{title}</h2>
      {children}
    </div>
  );
}

function inviteUrlForCode(code) {
  if (!code) return '';
  return `${window.location.origin}/signup?code=${encodeURIComponent(code)}`;
}

function inviteStatus(invite) {
  if (invite.revoked_at) return 'revoked';
  if (invite.used_at) return 'joined';
  return 'pending';
}

function inviteTerminology(message) {
  return message.replace(/join\s+code/gi, 'invite code');
}

// ─── Toggle Row ────────────────────────────────────────────────────────────

function ToggleRow({ label, description, checked, onChange, disabled = false }) {
  return (
    <div className={`settings-toggle-row ${disabled ? 'settings-toggle-row--disabled' : ''}`}>
      <div className="settings-toggle-text">
        <span className="settings-toggle-label">{label}</span>
        {description && <span className="settings-toggle-desc">{description}</span>}
      </div>
      <button
        role="switch"
        aria-checked={checked}
        className={`settings-toggle-btn ${checked ? 'settings-toggle-btn--on' : ''}`}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
      >
        <span className="settings-toggle-knob" />
      </button>
    </div>
  );
}

function GettingStartedCard() {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('gettingStartedDismissed') === '1');
  const [open, setOpen] = useState(false);

  if (dismissed) return null;

  function dismiss() {
    localStorage.setItem('gettingStartedDismissed', '1');
    setDismissed(true);
  }

  return (
    <SectionCard title="Getting started">
      <button type="button" className="settings-collapse-row" onClick={() => setOpen(o => !o)}>
        <span>Household basics</span>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} className="collapse-icon" />
      </button>
      {open && (
        <div className="settings-start-content">
          <p>
            A household is the shared collection space for your plants. Owners and admins can invite others with single-use invite codes from Manage Household.
          </p>
          <div className="settings-start-links">
            <Link to="/new">Add Plant</Link>
            <Link to="/explore">Explore</Link>
            <Link to="/rounds">Rounds</Link>
          </div>
          <button type="button" className="settings-lookup-action settings-lookup-action--ghost" onClick={dismiss}>
            Got it
          </button>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Field Options ─────────────────────────────────────────────────────────

function AddLookupForm({ placeholder, disabled, onAdd }) {
  const [name, setName] = useState('');

  async function submit(e) {
    e.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    const ok = await onAdd(clean);
    if (ok) setName('');
  }

  return (
    <form className="settings-lookup-add" onSubmit={submit}>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={placeholder}
        className="settings-lookup-input"
        disabled={disabled}
      />
      <button className="settings-lookup-add-btn" disabled={disabled || !name.trim()}>
        Add
      </button>
    </form>
  );
}

function LookupRow({ item, busy, canRename, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const usageCount = Number(item.usage_count || 0);
  const inUse = usageCount > 0;

  useEffect(() => { setDraft(item.name); }, [item.name]);

  async function saveRename(e) {
    e.preventDefault();
    const clean = draft.trim();
    if (!clean || clean === item.name) { setEditing(false); setDraft(item.name); return; }
    const ok = await onRename(item, clean);
    if (ok) setEditing(false);
  }

  if (editing) {
    return (
      <form className="fo-row fo-row--editing" onSubmit={saveRename}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          className="settings-lookup-input fo-edit-input"
          autoFocus
          disabled={busy}
        />
        <button className="settings-lookup-action" disabled={busy || !draft.trim()}>Save</button>
        <button
          type="button"
          className="settings-lookup-action settings-lookup-action--ghost"
          onClick={() => { setEditing(false); setDraft(item.name); }}
          disabled={busy}
        >Cancel</button>
      </form>
    );
  }

  if (confirmingDelete) {
    return (
      <div className="inline-confirm" style={{ margin: '0.15rem 0' }}>
        <span className="inline-confirm-msg">Delete "{item.name}"?</span>
        <div className="inline-confirm-actions">
          <button className="inline-confirm-no" onClick={() => setConfirmingDelete(false)}>Cancel</button>
          <button className="inline-confirm-yes" onClick={() => { setConfirmingDelete(false); onDelete(item); }}>Delete</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fo-row">
      <span className="fo-row-name">{item.name}</span>
      <div className="fo-row-right">
        <span className={`fo-use-badge ${inUse ? 'fo-use-badge--used' : ''}`}>
          {usageCount}
        </span>
        <div className="fo-row-actions">
          {canRename && (
            <button className="fo-icon-btn" onClick={() => setEditing(true)} disabled={busy} title="Rename">
              <Icon name="pencil" size={14} />
            </button>
          )}
          <button
            className="fo-icon-btn fo-icon-btn--danger"
            onClick={() => setConfirmingDelete(true)}
            disabled={busy || inUse}
            title={inUse ? `Used by ${usageCount} plant${usageCount === 1 ? '' : 's'} — reassign first` : 'Delete'}
          >
            <Icon name="trash" size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function LookupAccordion({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fo-section">
      <button className="fo-section-header" onClick={() => setOpen(o => !o)}>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} className="fo-section-chevron" />
        <span className="fo-section-title">{title}</span>
        <span className="fo-count-badge">{count}</span>
      </button>
      {open && <div className="fo-section-body">{children}</div>}
    </div>
  );
}

function MethodBlock({ method, busy, onRenameMethod, onDeleteMethod, onAddSubstrate, onRenameSubstrate, onDeleteSubstrate }) {
  const [editingMethod, setEditingMethod] = useState(false);
  const [methodDraft, setMethodDraft] = useState(method.name);
  const methodUsage = Number(method.usage_count || 0);
  const methodBusy = Boolean(busy[`growing-methods:${method.id}`]);

  useEffect(() => { setMethodDraft(method.name); }, [method.name]);

  async function saveMethodRename(e) {
    e.preventDefault();
    const clean = methodDraft.trim();
    if (!clean || clean === method.name) { setEditingMethod(false); setMethodDraft(method.name); return; }
    const ok = await onRenameMethod(method, clean);
    if (ok) setEditingMethod(false);
  }

  return (
    <div className="fo-method-block">
      <div className="fo-method-header">
        {editingMethod ? (
          <form className="fo-row fo-row--editing" style={{ flex: 1 }} onSubmit={saveMethodRename}>
            <input
              value={methodDraft}
              onChange={e => setMethodDraft(e.target.value)}
              className="settings-lookup-input fo-edit-input"
              autoFocus
              disabled={methodBusy}
            />
            <button className="settings-lookup-action" disabled={methodBusy || !methodDraft.trim()}>Save</button>
            <button
              type="button"
              className="settings-lookup-action settings-lookup-action--ghost"
              onClick={() => { setEditingMethod(false); setMethodDraft(method.name); }}
              disabled={methodBusy}
            >Cancel</button>
          </form>
        ) : (
          <>
            <span className="fo-row-name">{method.name}</span>
            <div className="fo-row-right">
              <span className={`fo-use-badge ${methodUsage ? 'fo-use-badge--used' : ''}`}>
                {methodUsage}
              </span>
              <div className="fo-row-actions">
                <button className="fo-icon-btn" onClick={() => setEditingMethod(true)} disabled={methodBusy} title="Rename">
                  <Icon name="pencil" size={14} />
                </button>
                <button
                  className="fo-icon-btn fo-icon-btn--danger"
                  onClick={() => onDeleteMethod(method)}
                  disabled={methodBusy || methodUsage > 0}
                  title={methodUsage > 0 ? `Used by ${methodUsage} plant${methodUsage === 1 ? '' : 's'} — reassign first` : 'Delete'}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="fo-substrates">
        <AddLookupForm
          placeholder={`New ${method.name} substrate`}
          disabled={Boolean(busy[`substrates:add:${method.id}`])}
          onAdd={name => onAddSubstrate(method.id, name)}
        />
        {(method.substrates || []).map(substrate => (
          <LookupRow
            key={substrate.id}
            item={substrate}
            busy={Boolean(busy[`substrates:${substrate.id}`])}
            canRename
            onRename={(s, name) => onRenameSubstrate(s, name)}
            onDelete={s => onDeleteSubstrate(s)}
          />
        ))}
        {(method.substrates || []).length === 0 && (
          <p className="fo-empty fo-empty--sub">No substrates yet.</p>
        )}
      </div>
    </div>
  );
}


export function FieldOptions() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lookupBusy, setLookupBusy] = useState({});
  const [lookupError, setLookupError] = useState({});
  const [lookups, setLookups] = useState({
    locations: [],
    tags: [],
    lightSources: [],
    growingMethods: [],
    fieldOptions: {},
  });

  const refreshLookups = useCallback(async () => {
    const [locations, tags, lightSources, growingMethods, fieldOptions] = await Promise.all([
      fetch('/locations').then(r => { if (!r.ok) throw new Error('Failed to load locations'); return r.json(); }),
      fetch('/tags').then(r => { if (!r.ok) throw new Error('Failed to load tags'); return r.json(); }),
      fetch('/light-sources').then(r => { if (!r.ok) throw new Error('Failed to load light sources'); return r.json(); }),
      fetch('/growing-methods').then(r => { if (!r.ok) throw new Error('Failed to load growing methods'); return r.json(); }),
      fetch('/field-options').then(r => { if (!r.ok) throw new Error('Failed to load field options'); return r.json(); }),
    ]);
    setLookups({ locations, tags, lightSources, growingMethods, fieldOptions });
  }, []);

  useEffect(() => {
    refreshLookups()
      .then(() => setLoading(false))
      .catch(err => { setError(err.message); setLoading(false); });
  }, [refreshLookups]);

  async function lookupRequest(busyKey, errorKey, url, options = {}) {
    setLookupBusy(s => ({ ...s, [busyKey]: true }));
    setLookupError(s => ({ ...s, [errorKey]: null }));
    try {
      const res = await fetch(url, options);
      if (!res.ok) throw new Error(await readApiError(res));
      await refreshLookups();
      notifyFieldOptionsChanged();
      return true;
    } catch (err) {
      setLookupError(s => ({ ...s, [errorKey]: err.message }));
      return false;
    } finally {
      setLookupBusy(s => ({ ...s, [busyKey]: false }));
    }
  }

  function addSimpleLookup(endpoint, key, name) {
    return lookupRequest(key, key, `/${endpoint}?name=${encodeURIComponent(name)}`, { method: 'POST' });
  }

  function renameSimpleLookup(endpoint, key, item, name) {
    return lookupRequest(`${key}:${item.id}`, key, `/${endpoint}/${item.id}?name=${encodeURIComponent(name)}`, { method: 'PUT' });
  }

  function deleteLookup(endpoint, key, item) {
    return lookupRequest(`${key}:${item.id}`, key, `/${endpoint}/${item.id}`, { method: 'DELETE' });
  }

  function addFieldOption(groupKey, name) {
    return lookupRequest(`field-options:${groupKey}`, `field-options:${groupKey}`, `/field-options/${groupKey}?name=${encodeURIComponent(name)}`, { method: 'POST' });
  }

  function renameFieldOption(item, name) {
    return lookupRequest(`field-options:${item.id}`, `field-options:${item.group_key}`, `/field-options/${item.id}?name=${encodeURIComponent(name)}`, { method: 'PUT' });
  }

  function deleteFieldOption(item) {
    return lookupRequest(`field-options:${item.id}`, `field-options:${item.group_key}`, `/field-options/${item.id}`, { method: 'DELETE' });
  }

  if (loading) return <div className="app"><p>Loading field options...</p></div>;
  if (error) return <div className="app"><ErrorScreen message={error} onRetry={() => { setLoading(true); setError(null); refreshLookups().then(() => setLoading(false)).catch(e => { setError(e.message); setLoading(false); }); }} /></div>;

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/settings', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Field Options</h1>
        </div>
      </header>

      <div className="fo-sections">
        {[
          {
            title: 'Growing Methods',
            count: lookups.growingMethods.length,
            body: (
              <>
                <AddLookupForm
                  placeholder="New growing method"
                  disabled={Boolean(lookupBusy['growing-methods'])}
                  onAdd={name => addSimpleLookup('growing-methods', 'growing-methods', name)}
                />
                {(lookupError['growing-methods'] || lookupError.substrates) && (
                  <p className="settings-save-error">
                    {lookupError['growing-methods'] || lookupError.substrates}
                  </p>
                )}
                <div className="fo-list">
                  {lookups.growingMethods.map(method => (
                    <MethodBlock
                      key={method.id}
                      method={method}
                      busy={lookupBusy}
                      onRenameMethod={(m, name) => lookupRequest(
                        `growing-methods:${m.id}`, 'growing-methods',
                        `/growing-methods/${m.id}?name=${encodeURIComponent(name)}`, { method: 'PUT' }
                      )}
                      onDeleteMethod={m => deleteLookup('growing-methods', 'growing-methods', m)}
                      onAddSubstrate={(methodId, name) => lookupRequest(
                        `substrates:add:${methodId}`, 'substrates',
                        `/substrates?name=${encodeURIComponent(name)}&growing_method_id=${methodId}`, { method: 'POST' }
                      )}
                      onRenameSubstrate={(s, name) => lookupRequest(
                        `substrates:${s.id}`, 'substrates',
                        `/substrates/${s.id}?name=${encodeURIComponent(name)}`, { method: 'PUT' }
                      )}
                      onDeleteSubstrate={s => deleteLookup('substrates', 'substrates', s)}
                    />
                  ))}
                  {lookups.growingMethods.length === 0 && <p className="fo-empty">No growing methods yet.</p>}
                </div>
              </>
            ),
          },
          {
            title: 'Light Sources',
            count: lookups.lightSources.length,
            body: (
              <>
                <AddLookupForm
                  placeholder="New light source"
                  disabled={Boolean(lookupBusy['light-sources'])}
                  onAdd={name => addSimpleLookup('light-sources', 'light-sources', name)}
                />
                {lookupError['light-sources'] && <p className="settings-save-error">{lookupError['light-sources']}</p>}
                <div className="fo-list">
                  {lookups.lightSources.map(item => (
                    <LookupRow key={item.id} item={item} busy={Boolean(lookupBusy[`light-sources:${item.id}`])} canRename
                      onRename={(it, name) => renameSimpleLookup('light-sources', 'light-sources', it, name)}
                      onDelete={it => deleteLookup('light-sources', 'light-sources', it)} />
                  ))}
                  {lookups.lightSources.length === 0 && <p className="fo-empty">No light sources yet.</p>}
                </div>
              </>
            ),
          },
          {
            title: 'Locations',
            count: lookups.locations.length,
            body: (
              <>
                <AddLookupForm
                  placeholder="New location"
                  disabled={Boolean(lookupBusy.locations)}
                  onAdd={name => addSimpleLookup('locations', 'locations', name)}
                />
                {lookupError.locations && <p className="settings-save-error">{lookupError.locations}</p>}
                <div className="fo-list">
                  {lookups.locations.map(item => (
                    <LookupRow key={item.id} item={item} busy={Boolean(lookupBusy[`locations:${item.id}`])} canRename
                      onRename={(it, name) => renameSimpleLookup('locations', 'locations', it, name)}
                      onDelete={it => deleteLookup('locations', 'locations', it)} />
                  ))}
                  {lookups.locations.length === 0 && <p className="fo-empty">No locations yet.</p>}
                </div>
              </>
            ),
          },
          {
            title: 'Tags',
            count: lookups.tags.length,
            body: (
              <>
                <AddLookupForm
                  placeholder="New tag"
                  disabled={Boolean(lookupBusy.tags)}
                  onAdd={name => addSimpleLookup('tags', 'tags', name)}
                />
                {lookupError.tags && <p className="settings-save-error">{lookupError.tags}</p>}
                <div className="fo-list">
                  {lookups.tags.map(item => (
                    <LookupRow key={item.id} item={item} busy={Boolean(lookupBusy[`tags:${item.id}`])} canRename
                      onRename={(it, name) => renameSimpleLookup('tags', 'tags', it, name)}
                      onDelete={it => deleteLookup('tags', 'tags', it)} />
                  ))}
                  {lookups.tags.length === 0 && <p className="fo-empty">No tags yet.</p>}
                </div>
              </>
            ),
          },
          ...Object.entries(FIELD_OPTION_GROUP_LABELS)
            .filter(([groupKey]) => !['prop_hormone', 'prop_enclosure', 'loss_reason'].includes(groupKey))
            .map(([groupKey, label]) => {
              const options = lookups.fieldOptions[groupKey] || [];
              const busyKey = `field-options:${groupKey}`;
              return {
                title: label,
                count: options.length,
                body: (
                  <>
                    <AddLookupForm
                      placeholder={`New ${label.toLowerCase()}`}
                      disabled={Boolean(lookupBusy[busyKey])}
                      onAdd={name => addFieldOption(groupKey, name)}
                    />
                    {lookupError[busyKey] && <p className="settings-save-error">{lookupError[busyKey]}</p>}
                    <div className="fo-list">
                      {options.map(item => (
                        <LookupRow key={item.id} item={{ ...item, name: item.label }}
                          busy={Boolean(lookupBusy[`field-options:${item.id}`])} canRename
                          onRename={(it, name) => renameFieldOption(item, name)}
                          onDelete={() => deleteFieldOption(item)} />
                      ))}
                      {options.length === 0 && <p className="fo-empty">No options yet.</p>}
                    </div>
                  </>
                ),
              };
            }),
        ]
          .sort((a, b) => a.title.localeCompare(b.title))
          .map(({ title, count, body }) => (
            <LookupAccordion key={title} title={title} count={count}>
              {body}
            </LookupAccordion>
          ))
        }

        {/* ── Propagation field options ─────────────────────────────────── */}
        <div style={{ margin: '1.25rem 0 0.5rem', paddingBottom: '0.35rem', borderBottom: '1px solid var(--border, #e8e8e8)' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-xlight, #aaa)' }}>
            Propagation
          </span>
        </div>
        {Object.entries(FIELD_OPTION_GROUP_LABELS)
          .filter(([groupKey]) => ['prop_hormone', 'prop_enclosure', 'loss_reason'].includes(groupKey))
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([groupKey, label]) => {
            const options = lookups.fieldOptions[groupKey] || [];
            const busyKey = `field-options:${groupKey}`;
            return (
              <LookupAccordion key={groupKey} title={label} count={options.length}>
                <AddLookupForm
                  placeholder={`New ${label.toLowerCase()}`}
                  disabled={Boolean(lookupBusy[busyKey])}
                  onAdd={name => addFieldOption(groupKey, name)}
                />
                {lookupError[busyKey] && <p className="settings-save-error">{lookupError[busyKey]}</p>}
                <div className="fo-list">
                  {options.map(item => (
                    <LookupRow key={item.id} item={{ ...item, name: item.label }}
                      busy={Boolean(lookupBusy[`field-options:${item.id}`])} canRename
                      onRename={(it, name) => renameFieldOption(item, name)}
                      onDelete={() => deleteFieldOption(item)} />
                  ))}
                  {options.length === 0 && <p className="fo-empty">No options yet.</p>}
                </div>
              </LookupAccordion>
            );
          })
        }

      </div>
    </div>
  );
}

export function ExportData({ session }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState({});
  const [error, setError] = useState({});

  const householdSlug = (session?.active_household?.name || 'household')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const today = new Date().toISOString().slice(0, 10);

  function triggerDownload(csvString, filename) {
    const BOM = '﻿';
    const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  function escapeCell(v) {
    if (v == null) return '';
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  function toCSV(rows, headers, fields) {
    const lines = [
      headers.join(','),
      ...rows.map(r =>
        fields.map(f => escapeCell(typeof f === 'function' ? f(r) : r[f])).join(',')
      ),
    ];
    return lines.join('\n');
  }

  async function downloadPlants() {
    setBusy(b => ({ ...b, plants: true }));
    setError(e => ({ ...e, plants: null }));
    try {
      const data = await fetch('/plants').then(r => { if (!r.ok) throw new Error('Failed to fetch plants'); return r.json(); });
      const headers = [
        'Specimen ID','Genus','Species','Common Name','Status',
        'Acquisition Date','Source','Cost','Growing Method','Substrate',
        'Locations','Light Sources','Tags','Notes','Photos','Last Activity','Added',
      ];
      const fields = [
        'specimen_id','genus','species','common_name','status',
        'acquisition_date','source','cost','growing_method','substrate',
        r => (r.locations || []).map(l => l.name).join('; '),
        r => (r.light_sources || []).map(l => l.name).join('; '),
        r => (r.tags || []).map(t => t.name).join('; '),
        'notes','photo_count','last_activity','created_at',
      ];
      triggerDownload(toCSV(data, headers, fields), `${householdSlug}_plants_${today}.csv`);
    } catch (err) {
      setError(e => ({ ...e, plants: err.message }));
    } finally {
      setBusy(b => ({ ...b, plants: false }));
    }
  }

  async function downloadObservations() {
    setBusy(b => ({ ...b, observations: true }));
    setError(e => ({ ...e, observations: null }));
    try {
      const data = await fetch('/export/observations').then(r => { if (!r.ok) throw new Error('Failed to fetch observations'); return r.json(); });
      const headers = [
        'Specimen ID','Date','Event Type','Notes',
        'Pot Size','Pot Type','New Growing Method (repot)','New Substrate (repot)','Root Condition',
        'Fert Product','Concentration','Fert Method',
        'Treatment Category','Target','Treatment Product','Treatment Method',
        'Old Growing Method','Old Substrate','New Growing Method (substrate)','New Substrate (substrate)','Reason',
        'Accl Method','Source Condition','Accl Start','Accl End',
        'Prop Method','Reproduction Category',
      ];
      const fields = [
        'specimen_id','date','event_type','notes',
        'new_pot_size','pot_type','new_growing_method','new_substrate','root_condition',
        'fert_product','concentration','fert_method',
        'treatment_category','target','treat_product','treat_method',
        'old_growing_method','old_substrate','sc_new_method','sc_new_substrate','reason',
        'accl_method','source_condition','accl_start','accl_end',
        'prop_method','reproduction_category',
      ];
      triggerDownload(toCSV(data, headers, fields), `${householdSlug}_observations_${today}.csv`);
    } catch (err) {
      setError(e => ({ ...e, observations: err.message }));
    } finally {
      setBusy(b => ({ ...b, observations: false }));
    }
  }

  async function downloadMeasurements() {
    setBusy(b => ({ ...b, measurements: true }));
    setError(e => ({ ...e, measurements: null }));
    try {
      const data = await fetch('/export/measurements').then(r => { if (!r.ok) throw new Error('Failed to fetch measurements'); return r.json(); });
      const headers = ['Specimen ID','Date','Height (in)','Longest Leaf (in)','Leaf Count','Notes','Added'];
      const fields = ['specimen_id','date','height_inches','longest_leaf_inches','leaf_count','notes','created_at'];
      triggerDownload(toCSV(data, headers, fields), `${householdSlug}_measurements_${today}.csv`);
    } catch (err) {
      setError(e => ({ ...e, measurements: err.message }));
    } finally {
      setBusy(b => ({ ...b, measurements: false }));
    }
  }

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/settings', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Export Data</h1>
        </div>
      </header>

      <div className="settings-page">
        <SectionCard title="Plants">
          <p className="settings-link-desc" style={{ padding: '0.25rem 0 0.75rem' }}>
            All plants in your collection including locations, tags, and growing setup.
          </p>
          <button className="btn btn-secondary" onClick={downloadPlants} disabled={busy.plants}>
            {busy.plants ? 'Preparing…' : 'Download Plants CSV'}
          </button>
          {error.plants && <p className="settings-save-error">{error.plants}</p>}
        </SectionCard>

        <SectionCard title="Observations">
          <p className="settings-link-desc" style={{ padding: '0.25rem 0 0.75rem' }}>
            All care events across every plant, including repot, fertilize, treatment, and other detail fields.
          </p>
          <button className="btn btn-secondary" onClick={downloadObservations} disabled={busy.observations}>
            {busy.observations ? 'Preparing…' : 'Download Observations CSV'}
          </button>
          {error.observations && <p className="settings-save-error">{error.observations}</p>}
        </SectionCard>

        <SectionCard title="Measurements">
          <p className="settings-link-desc" style={{ padding: '0.25rem 0 0.75rem' }}>
            All height, leaf count, and longest-leaf measurements across every plant.
          </p>
          <button className="btn btn-secondary" onClick={downloadMeasurements} disabled={busy.measurements}>
            {busy.measurements ? 'Preparing…' : 'Download Measurements CSV'}
          </button>
          {error.measurements && <p className="settings-save-error">{error.measurements}</p>}
        </SectionCard>

        <div style={{ height: '1.5rem' }} />
      </div>
    </div>
  );
}

export function HouseholdAdmin({ session, onSessionChange }) {
  const navigate = useNavigate();
  const canManageCodes = session?.active_household?.role === 'owner' || session?.active_household?.role === 'admin';

  useEffect(() => {
    if (session && !canManageCodes) navigate('/settings', { replace: true });
  }, [session, canManageCodes, navigate]);

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/settings', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Manage Household</h1>
        </div>
      </header>

      <div className="settings-household-context">
        <strong>{session?.active_household?.name}</strong>
        <span className="settings-role-pill">{session?.active_household?.role}</span>
      </div>

      <HouseholdAdminPanel session={session} onSessionChange={onSessionChange} />

      <div style={{ height: '1.5rem' }} />
    </div>
  );
}

// ─── Nav Slot Picker ───────────────────────────────────────────────────────


// ─── Account Identity Section ─────────────────────────────────────────────

function AccountIdentitySection({ session, onSessionChange, onLogout }) {
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const user = session?.user;

  function startEditName() {
    setNameInput(user?.display_name || '');
    setNameError('');
    setEditingName(true);
  }

  async function saveDisplayName() {
    setNameSaving(true);
    setNameError('');
    try {
      const res = await apiFetch('/users/me', {
        method: 'PUT',
        body: JSON.stringify({ display_name: nameInput.trim() || null }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      onSessionChange?.(await res.json());
      setEditingName(false);
    } catch (err) {
      setNameError(err.message);
    } finally {
      setNameSaving(false);
    }
  }

  return (
    <div className="settings-account">
      <div className="settings-account-row">
        <div>
          <span className="settings-account-label">Signed in</span>
          {editingName ? (
            <div className="settings-inline-edit">
              <input
                className="settings-inline-input"
                type="text"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Display name (optional)"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(); if (e.key === 'Escape') setEditingName(false); }}
                disabled={nameSaving}
              />
              <div className="settings-inline-actions">
                <button className="settings-lookup-action" onClick={saveDisplayName} disabled={nameSaving}>
                  {nameSaving ? 'Saving…' : 'Save'}
                </button>
                <button className="settings-lookup-action settings-lookup-action--ghost" onClick={() => setEditingName(false)} disabled={nameSaving}>
                  Cancel
                </button>
              </div>
              {nameError && <p className="settings-save-error">{nameError}</p>}
            </div>
          ) : (
            <div className="settings-inline-display">
              <div className="settings-inline-name-row">
                <strong>{user?.display_name || user?.email}</strong>
                <button className="settings-lookup-action settings-lookup-action--ghost" onClick={startEditName}>
                  Edit
                </button>
              </div>
              {user?.display_name && <span>{user.email}</span>}
            </div>
          )}
        </div>
        <button className="settings-lookup-action settings-lookup-action--ghost" onClick={onLogout}>
          Log Out
        </button>
      </div>
    </div>
  );
}

// ─── Account Household Section ────────────────────────────────────────────

export function AccountHouseholdSection({ session, onSessionChange }) {
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [createHouseholdBusy, setCreateHouseholdBusy] = useState(false);
  const [createHouseholdError, setCreateHouseholdError] = useState('');
  const [joinHouseholdCode, setJoinHouseholdCode] = useState('');
  const [joinHouseholdBusy, setJoinHouseholdBusy] = useState(false);
  const [joinHouseholdError, setJoinHouseholdError] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const households = session?.households || [];
  const activeHousehold = session?.active_household;
  const canSwitch = households.length > 1;
  const canManageCodes = activeHousehold?.role === 'owner' || activeHousehold?.role === 'admin';

  function clearHouseholdViewState() {
    sessionStorage.removeItem('plantListState');
    sessionStorage.removeItem('plantListScroll');
  }

  async function switchHousehold(householdId) {
    const nextId = Number(householdId);
    if (!nextId || nextId === activeHousehold?.id) return;
    setSwitching(true);
    setError('');
    try {
      const res = await apiFetch('/auth/session/household', {
        method: 'POST',
        body: JSON.stringify({ household_id: nextId }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      onSessionChange?.(await res.json());
      clearHouseholdViewState();
    } catch (err) {
      setError(err.message);
    } finally {
      setSwitching(false);
    }
  }

  async function createHousehold() {
    setCreateHouseholdBusy(true);
    setCreateHouseholdError('');
    try {
      const res = await apiFetch('/households', {
        method: 'POST',
        body: JSON.stringify({ name: newHouseholdName.trim() }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      onSessionChange?.(await res.json());
      setNewHouseholdName('');
      clearHouseholdViewState();
    } catch (err) {
      setCreateHouseholdError(err.message);
    } finally {
      setCreateHouseholdBusy(false);
    }
  }

  async function joinHousehold() {
    setJoinHouseholdBusy(true);
    setJoinHouseholdError('');
    try {
      const res = await apiFetch('/households/join', {
        method: 'POST',
        body: JSON.stringify({ join_code: joinHouseholdCode.trim() }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      onSessionChange?.(await res.json());
      setJoinHouseholdCode('');
      clearHouseholdViewState();
    } catch (err) {
      setJoinHouseholdError(inviteTerminology(err.message));
    } finally {
      setJoinHouseholdBusy(false);
    }
  }

  return (
    <div className="settings-account">
      <div className="settings-account-row">
        <div>
          <span className="settings-account-label">Household</span>
          <strong>{activeHousehold?.name || 'Household'}</strong>
          {activeHousehold?.role && <span className="settings-role-pill">{activeHousehold.role}</span>}
        </div>
      </div>

      {canSwitch && (
        <label className="settings-household-switch">
          <span className="settings-field-label">Active Household</span>
          <select
            className="sort-select"
            value={activeHousehold?.id || ''}
            onChange={e => switchHousehold(e.target.value)}
            disabled={switching}
          >
            {households.map(household => (
              <option key={household.id} value={household.id}>
                {household.name} ({household.role})
              </option>
            ))}
          </select>
        </label>
      )}

      {switching && <p className="settings-save-status">Switching household...</p>}
      {error && <p className="settings-save-error">{error}</p>}

      <button type="button" className="settings-collapse-row" onClick={() => setManageOpen(o => !o)}>
        <span>Manage Households</span>
        <Icon name={manageOpen ? 'chevronDown' : 'chevronRight'} size={16} className="collapse-icon" />
      </button>

      {manageOpen && (
        <div className="settings-manage-households">
          <div className="settings-inline-edit">
            <input
              className="settings-inline-input"
              type="text"
              value={joinHouseholdCode}
              onChange={e => setJoinHouseholdCode(e.target.value)}
              placeholder="Invite code"
              autoCapitalize="characters"
              autoComplete="off"
              disabled={joinHouseholdBusy}
              onKeyDown={e => { if (e.key === 'Enter' && joinHouseholdCode.trim()) joinHousehold(); }}
            />
            <div className="settings-inline-actions">
              <button
                className="settings-lookup-action"
                onClick={joinHousehold}
                disabled={joinHouseholdBusy || !joinHouseholdCode.trim()}
              >
                {joinHouseholdBusy ? 'Joining…' : 'Join Household'}
              </button>
            </div>
            {joinHouseholdError && <p className="settings-save-error">{joinHouseholdError}</p>}
          </div>
          <div className="settings-inline-edit">
            <input
              className="settings-inline-input"
              type="text"
              value={newHouseholdName}
              onChange={e => setNewHouseholdName(e.target.value)}
              placeholder="New separate household name"
              disabled={createHouseholdBusy}
              onKeyDown={e => { if (e.key === 'Enter' && newHouseholdName.trim()) createHousehold(); }}
            />
            <div className="settings-inline-actions">
              <button
                className="settings-lookup-action"
                onClick={createHousehold}
                disabled={createHouseholdBusy || !newHouseholdName.trim()}
              >
                {createHouseholdBusy ? 'Creating…' : 'Create Household'}
              </button>
            </div>
            {createHouseholdError && <p className="settings-save-error">{createHouseholdError}</p>}
          </div>
        </div>
      )}

      {canManageCodes && (
        <Link to="/settings/household-admin" className="settings-link-row">
          <div>
            <span className="settings-link-title">Manage Household</span>
            <span className="settings-link-desc">Members, invites, and household settings</span>
          </div>
          <span className="settings-link-action">Manage</span>
        </Link>
      )}
    </div>
  );
}

// ─── Household Admin Panel ────────────────────────────────────────────────

function HouseholdAdminPanel({ session, onSessionChange }) {
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [invitesError, setInvitesError] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMethod, setInviteMethod] = useState('manual');
  const [inviteLabel, setInviteLabel] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteSheet, setInviteSheet] = useState(null);
  const [copiedInviteId, setCopiedInviteId] = useState(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const [confirmResetId, setConfirmResetId] = useState(null);
  const [resendingInviteId, setResendingInviteId] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [resetLinks, setResetLinks] = useState({});
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [editingHousehold, setEditingHousehold] = useState(false);
  const [householdInput, setHouseholdInput] = useState('');
  const [householdSaving, setHouseholdSaving] = useState(false);
  const [householdError, setHouseholdError] = useState('');
  const activeHousehold = session?.active_household;
  const canManageCodes = activeHousehold?.role === 'owner' || activeHousehold?.role === 'admin';

  function formatJoinedAt(value) {
    if (!value) return null;
    const parsed = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  const loadInvites = useCallback(async () => {
    if (!canManageCodes) return;
    setInvitesLoading(true);
    setInvitesError('');
    try {
      const res = await apiFetch('/household/join-codes');
      if (!res.ok) throw new Error(await readApiError(res));
      setInvites(await res.json());
    } catch (err) {
      setInvitesError(err.message);
    } finally {
      setInvitesLoading(false);
    }
  }, [canManageCodes]);

  const loadMembers = useCallback(async () => {
    if (!canManageCodes) return;
    setMembersLoading(true);
    setMembersError('');
    try {
      const res = await apiFetch('/household/members');
      if (!res.ok) throw new Error(await readApiError(res));
      setMembers(await res.json());
    } catch (err) {
      setMembersError(err.message);
    } finally {
      setMembersLoading(false);
    }
  }, [canManageCodes]);

  const loadAuditEvents = useCallback(async () => {
    if (!canManageCodes) return;
    setAuditLoading(true);
    setAuditError('');
    try {
      const res = await apiFetch('/household/activity?limit=20');
      if (!res.ok) throw new Error(await readApiError(res));
      setAuditEvents(await res.json());
    } catch (err) {
      setAuditError(err.message);
    } finally {
      setAuditLoading(false);
    }
  }, [canManageCodes]);

  useEffect(() => { loadInvites(); }, [loadInvites]);
  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { loadAuditEvents(); }, [loadAuditEvents]);

  async function generateResetLink(userId) {
    setConfirmResetId(null);
    setResetLinks(prev => ({ ...prev, [userId]: { loading: true, url: null, error: null } }));
    try {
      const res = await apiFetch(`/household/members/${userId}/reset-link`, { method: 'POST' });
      if (!res.ok) throw new Error(await readApiError(res));
      const data = await res.json();
      setResetLinks(prev => ({ ...prev, [userId]: { loading: false, url: data.reset_url, error: null } }));
    } catch (err) {
      setResetLinks(prev => ({ ...prev, [userId]: { loading: false, url: null, error: err.message } }));
    }
  }

  async function createInvite({ role = inviteRole, method = inviteMethod, label = inviteLabel, email = inviteEmail, openSheet = true } = {}) {
    if (!activeHousehold?.id) return null;
    setCreatingInvite(true);
    setInvitesError('');
    setInviteSuccess('');
    try {
      const isEmail = method === 'email';
      const res = await apiFetch(`/households/${activeHousehold.id}/invites`, {
        method: 'POST',
        body: JSON.stringify({
          role,
          label: isEmail ? null : (label.trim() || null),
          invited_email: isEmail ? email.trim() : null,
        }),
      });
      if (!res.ok) {
        if (res.status === 429) throw new Error("Daily email invite limit reached. Use 'Share a code' instead.");
        throw new Error(await readApiError(res));
      }
      const created = await res.json();
      setInvites(prev => [created, ...prev]);
      if (isEmail) {
        setInviteSuccess(`Invite created for ${email.trim()}`);
        setInviteEmail('');
      } else {
        setInviteLabel('');
        if (openSheet) {
          setInviteSheet({
            code: created.code,
            inviteUrl: created.invite_url || inviteUrlForCode(created.code),
            householdName: activeHousehold?.name || 'Household',
          });
        }
      }
      loadAuditEvents();
      return created;
    } catch (err) {
      setInvitesError(err.message);
      return null;
    } finally {
      setCreatingInvite(false);
    }
  }

  async function revokeInvite(invite) {
    setInvitesError('');
    try {
      const res = await apiFetch(`/household/join-codes/${invite.id}/revoke`, { method: 'POST' });
      if (!res.ok) throw new Error(await readApiError(res));
      const updated = await res.json();
      setInvites(prev => prev.map(item => item.id === updated.id ? updated : item));
      setConfirmRevokeId(null);
      loadAuditEvents();
    } catch (err) {
      setInvitesError(err.message);
    }
  }

  async function copyInviteLink(invite) {
    const url = invite.invite_url || inviteUrlForCode(invite.code);
    if (await copyToClipboard(url)) {
      setCopiedInviteId(invite.id);
      window.setTimeout(() => setCopiedInviteId(current => current === invite.id ? null : current), 1500);
    } else {
      setInvitesError('Copy failed. Open the QR sheet and copy the code manually.');
    }
  }

  async function resendInvite(invite) {
    setResendingInviteId(invite.id);
    const created = await createInvite({
      role: invite.role,
      method: 'email',
      email: invite.invited_email || '',
      openSheet: false,
    });
    if (created) setInviteSuccess(`Invite created for ${invite.invited_email}`);
    setResendingInviteId(null);
  }

  function startEditHousehold() {
    setHouseholdInput(activeHousehold?.name || '');
    setHouseholdError('');
    setEditingHousehold(true);
  }

  async function saveHouseholdName() {
    setHouseholdSaving(true);
    setHouseholdError('');
    try {
      const res = await apiFetch('/household', {
        method: 'PUT',
        body: JSON.stringify({ name: householdInput.trim() }),
      });
      if (!res.ok) throw new Error(await readApiError(res));
      onSessionChange?.(await res.json());
      setEditingHousehold(false);
      loadAuditEvents();
    } catch (err) {
      setHouseholdError(err.message);
    } finally {
      setHouseholdSaving(false);
    }
  }

  if (!canManageCodes) return null;

  return (
    <>
      <div className="settings-member-list">
        <div className="settings-join-codes-header">
          <div>
            <span className="settings-account-label">Household Name</span>
            {editingHousehold ? (
              <div className="settings-inline-edit">
                <input
                  className="settings-inline-input"
                  type="text"
                  value={householdInput}
                  onChange={e => setHouseholdInput(e.target.value)}
                  placeholder="Household name"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveHouseholdName(); if (e.key === 'Escape') setEditingHousehold(false); }}
                  disabled={householdSaving}
                />
                <div className="settings-inline-actions">
                  <button className="settings-lookup-action" onClick={saveHouseholdName} disabled={householdSaving || !householdInput.trim()}>
                    {householdSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button className="settings-lookup-action settings-lookup-action--ghost" onClick={() => setEditingHousehold(false)} disabled={householdSaving}>
                    Cancel
                  </button>
                </div>
                {householdError && <p className="settings-save-error">{householdError}</p>}
              </div>
            ) : (
              <div className="settings-inline-display">
                <strong>{activeHousehold?.name || 'Household'}</strong>
                <button className="settings-lookup-action settings-lookup-action--ghost" onClick={startEditHousehold}>
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="settings-member-list">
        <div className="settings-join-codes-header">
          <div>
            <span className="settings-account-label">Members</span>
            <strong>Household Accounts</strong>
          </div>
          <button className="settings-lookup-action settings-lookup-action--ghost" onClick={loadMembers}>
            Refresh
          </button>
        </div>

        {membersLoading && <p className="settings-save-status">Loading members...</p>}
        {membersError && <p className="settings-save-error">{membersError}</p>}

        <div className="settings-member-rows">
          {members.map(member => {
            const rl = resetLinks[member.user_id];
            return (
              <div key={member.user_id} className="settings-member-row settings-member-row--wrap">
                <div>
                  <strong>{member.display_name || member.email}</strong>
                  {member.display_name && <span>{member.email}</span>}
                  {formatJoinedAt(member.joined_at) && <span>Joined {formatJoinedAt(member.joined_at)}</span>}
                </div>
                <div className="settings-member-actions">
                  <span className="settings-role-pill">{member.role}</span>
                  {rl?.url ? (
                    <>
                      <div className="settings-reset-link-row">
                        <input
                          className="settings-reset-link-input"
                          readOnly
                          value={rl.url}
                          onFocus={e => e.target.select()}
                        />
                        <button
                          type="button"
                          className="settings-lookup-action"
                          onClick={() => navigator.clipboard?.writeText(rl.url)}
                        >
                          Copy
                        </button>
                      </div>
                      <p className="settings-reset-link-expiry">This link expires in 24 hours.</p>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="settings-lookup-action"
                      onClick={() => setConfirmResetId(member.user_id)}
                      disabled={rl?.loading || confirmResetId === member.user_id}
                    >
                      {rl?.loading ? 'Generating…' : 'Reset link'}
                    </button>
                  )}
                  {rl?.error && <p className="settings-save-error">{rl.error}</p>}
                </div>
                {confirmResetId === member.user_id && (
                  <div className="inline-confirm settings-member-confirm">
                    <span className="inline-confirm-msg">Generate a 24-hour password reset link?</span>
                    <div className="inline-confirm-actions">
                      <button type="button" className="inline-confirm-no" onClick={() => setConfirmResetId(null)}>Cancel</button>
                      <button type="button" className="inline-confirm-yes" onClick={() => generateResetLink(member.user_id)}>Generate link</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="settings-join-codes">
        <div className="settings-join-codes-header">
          <div>
            <span className="settings-account-label">Invite Codes</span>
            <strong>Pending Invites</strong>
          </div>
        </div>

        <form
          className="settings-invite-form"
          onSubmit={e => {
            e.preventDefault();
            if (inviteMethod === 'email' && !inviteEmail.trim()) return;
            createInvite();
          }}
        >
          <div className="settings-field-label">Role</div>
          <div className="settings-segment settings-invite-role">
            <button
              type="button"
              className={`settings-segment-btn ${inviteRole === 'member' ? 'settings-segment-btn--active' : ''}`}
              onClick={() => setInviteRole('member')}
              disabled={creatingInvite}
            >
              Member
            </button>
            <button
              type="button"
              className={`settings-segment-btn ${inviteRole === 'admin' ? 'settings-segment-btn--active' : ''}`}
              onClick={() => setInviteRole('admin')}
              disabled={creatingInvite}
            >
              Admin
            </button>
          </div>

          <div className="settings-segment settings-invite-method">
            <button
              type="button"
              className={`settings-segment-btn ${inviteMethod === 'manual' ? 'settings-segment-btn--active' : ''}`}
              onClick={() => { setInviteMethod('manual'); setInviteEmail(''); setInvitesError(''); }}
              disabled={creatingInvite}
            >
              Share a code
            </button>
            <button
              type="button"
              className={`settings-segment-btn ${inviteMethod === 'email' ? 'settings-segment-btn--active' : ''}`}
              onClick={() => { setInviteMethod('email'); setInviteLabel(''); setInvitesError(''); }}
              disabled={creatingInvite}
            >
              Invite by email
            </button>
          </div>

          {inviteMethod === 'manual' ? (
            <input
              className="settings-inline-input"
              type="text"
              value={inviteLabel}
              onChange={e => setInviteLabel(e.target.value.slice(0, 100))}
              placeholder="Label (optional)"
              maxLength={100}
              disabled={creatingInvite}
            />
          ) : (
            <input
              className="settings-inline-input"
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              required
              disabled={creatingInvite}
            />
          )}

          <button
            type="submit"
            className="settings-lookup-action settings-invite-submit"
            disabled={creatingInvite || (inviteMethod === 'email' && !inviteEmail.trim())}
          >
            {creatingInvite ? 'Creating...' : (inviteMethod === 'email' ? 'Send invite' : 'Create invite')}
          </button>
        </form>

        {inviteSuccess && <p className="settings-save-status">{inviteSuccess}</p>}
        {invitesLoading && <p className="settings-save-status">Loading invite codes...</p>}
        {invitesError && <p className="settings-save-error">{invitesError}</p>}

        <div className="settings-invite-list">
          {invites.length === 0 && !invitesLoading ? (
            <p className="settings-empty-note">No pending invites. Create one above.</p>
          ) : (
            invites.map(invite => {
              const status = inviteStatus(invite);
              const method = invite.delivery_method === 'email' ? 'email' : 'manual';
              const inviteUrl = invite.invite_url || inviteUrlForCode(invite.code);
              return (
                <div key={invite.id} className={`settings-invite-card settings-invite-card--${status}`}>
                  <div className="settings-invite-card-primary">
                    <strong>{invite.invited_email || invite.label || invite.code}</strong>
                    {!invite.invited_email && invite.label && <code>{invite.code}</code>}
                  </div>
                  <div className="settings-invite-card-meta">
                    <span className="settings-invite-cap">{invite.role}</span>
                    <span className="settings-invite-meta-sep">·</span>
                    <span>{method === 'email' ? 'Email' : 'Share'}</span>
                    <span className="settings-invite-meta-sep">·</span>
                    <span>{formatJoinedAt(invite.created_at) || '-'}</span>
                    <span className="settings-invite-meta-sep">·</span>
                    <span className={`settings-invite-status-badge settings-invite-status-badge--${status}`}>
                      {status === 'joined' && invite.used_at ? `Joined ${formatJoinedAt(invite.used_at)}` : status}
                    </span>
                  </div>
                  {status === 'pending' && (
                    <div className="settings-invite-card-actions">
                      {method === 'manual' && (
                        <>
                          <button type="button" className="settings-lookup-action" onClick={() => copyInviteLink(invite)}>
                            {copiedInviteId === invite.id ? 'Copied!' : 'Copy link'}
                          </button>
                          <button
                            type="button"
                            className="settings-lookup-action"
                            onClick={() => setInviteSheet({
                              code: invite.code,
                              inviteUrl,
                              householdName: activeHousehold?.name || 'Household',
                            })}
                          >
                            QR
                          </button>
                        </>
                      )}
                      {method === 'email' && (
                        <button
                          type="button"
                          className="settings-lookup-action"
                          onClick={() => resendInvite(invite)}
                          disabled={resendingInviteId === invite.id || !invite.invited_email}
                        >
                          {resendingInviteId === invite.id ? 'Creating...' : 'Resend'}
                        </button>
                      )}
                      <button type="button" className="settings-lookup-action settings-lookup-action--danger" onClick={() => setConfirmRevokeId(invite.id)}>
                        Revoke
                      </button>
                    </div>
                  )}
                  {confirmRevokeId === invite.id && (
                    <div className="inline-confirm settings-invite-confirm">
                      <span className="inline-confirm-msg">Revoke this invite?</span>
                      <div className="inline-confirm-actions">
                        <button type="button" className="inline-confirm-no" onClick={() => setConfirmRevokeId(null)}>Cancel</button>
                        <button type="button" className="inline-confirm-yes" onClick={() => revokeInvite(invite)}>Revoke</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {inviteSheet && (
          <QRCodeSheet
            code={inviteSheet.code}
            inviteUrl={inviteSheet.inviteUrl}
            householdName={inviteSheet.householdName}
            onClose={() => setInviteSheet(null)}
          />
        )}
      </div>

      <div className="settings-join-codes">
        <div className="settings-join-codes-header">
          <div>
            <span className="settings-account-label">Admin Activity</span>
            <strong>Household Audit</strong>
          </div>
          <button className="settings-lookup-action settings-lookup-action--ghost" onClick={loadAuditEvents}>
            Refresh
          </button>
        </div>

        {auditLoading && <p className="settings-save-status">Loading activity...</p>}
        {auditError && <p className="settings-save-error">{auditError}</p>}

        <div className="settings-code-list">
          {auditEvents.length === 0 && !auditLoading ? (
            <p className="settings-empty-note">No admin activity yet.</p>
          ) : (
            auditEvents.map(event => (
              <div key={event.id} className="settings-code-row">
                <div>
                  <strong>{event.summary || event.action.replaceAll('_', ' ')}</strong>
                  <span>
                    {event.actor?.display_name || event.actor?.email || 'Unknown user'}
                    {event.created_at ? ` · ${formatJoinedAt(event.created_at)}` : ''}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

// ─── Settings Screen ───────────────────────────────────────────────────────

export default function Settings({ session, onSessionChange, onLogout }) {
  const { themePref, setThemePref } = useTheme();
  const { dateFmt, setDateFmt } = useDateFormat();
  const { resetAllTips } = useGuidance();

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [saving, setSaving]     = useState({}); // key → true while saving
  const [saveError, setSaveError] = useState({}); // key → error message
  const [resetTipsDone, setResetTipsDone] = useState(false);
  const [confirmResetTips, setConfirmResetTips] = useState(false);

  // Integration toggles
  const [plantnetSpecies,  setPlantnetSpecies]  = useState(true);

  const loadSettings = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch('/api/settings')
      .then(r => { if (!r.ok) throw new Error('Failed to load settings'); return r.json(); })
      .then(data => {
        if (data.theme)                    setThemePref(data.theme);
        if (data.plantnet_species_enabled) setPlantnetSpecies(data.plantnet_species_enabled === 'true');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [setThemePref]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // Persist a single setting key to the API
  async function saveSetting(key, value) {
    setSaving(s => ({ ...s, [key]: true }));
    setSaveError(s => ({ ...s, [key]: null }));
    try {
      const res = await fetch(`/api/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: String(value) }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
    } catch (err) {
      setSaveError(s => ({ ...s, [key]: err.message }));
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  }

  function handleThemeChange(pref) {
    setThemePref(pref);
    saveSetting('theme', pref);
  }

  async function handleDateFormatChange(fmt) {
    setDateFmt(fmt);
    setSaving(s => ({ ...s, date_format: true }));
    setSaveError(s => ({ ...s, date_format: null }));
    try {
      const res = await apiFetch('/users/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date_format: fmt }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const updated = await res.json();
      if (onSessionChange) onSessionChange(updated);
    } catch (err) {
      setSaveError(s => ({ ...s, date_format: err.message }));
    } finally {
      setSaving(s => ({ ...s, date_format: false }));
    }
  }

  function handlePlantnetSpeciesChange(val) {
    setPlantnetSpecies(val);
    saveSetting('plantnet_species_enabled', val);
  }

  async function handleResetTips() {
    setConfirmResetTips(false);
    setResetTipsDone(false);
    setSaving(s => ({ ...s, guidance_reset: true }));
    setSaveError(s => ({ ...s, guidance_reset: null }));
    const result = await resetAllTips();
    setSaving(s => ({ ...s, guidance_reset: false }));
    if (!result.ok) {
      setSaveError(s => ({ ...s, guidance_reset: result.error || 'Reset failed' }));
      return;
    }
    setResetTipsDone(true);
    window.setTimeout(() => {
      setResetTipsDone(false);
    }, 3000);
  }

  if (loading) return <div className="app"><p>Loading settings...</p></div>;
  if (error)   return <div className="app"><ErrorScreen message={error} onRetry={loadSettings} /></div>;

  const themeOptions = [
    { value: 'system', label: 'System' },
    { value: 'light',  label: 'Light' },
    { value: 'dark',   label: 'Dark' },
  ];

  return (
    <div className="app">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="app-header">
        <div className="app-header-brand">
          <img src="/logo_only.svg" alt="Aroidex" className="app-header-logo" />
          <div>
            <h1>Settings</h1>
          </div>
        </div>
      </header>

      {/* ── Account ─────────────────────────────────────────────────────── */}
      <SectionCard title="Account">
        <AccountIdentitySection
          session={session}
          onSessionChange={onSessionChange}
          onLogout={onLogout}
        />
      </SectionCard>

      {/* ── Household ───────────────────────────────────────────────────── */}
      <SectionCard title="Household">
        <AccountHouseholdSection
          session={session}
          onSessionChange={onSessionChange}
        />
      </SectionCard>

      {/* ── Preferences ─────────────────────────────────────────────────── */}
      <SectionCard title="Preferences">
        <div className="settings-field-label">Theme</div>
        <div className="settings-segment">
          {themeOptions.map(opt => (
            <button
              key={opt.value}
              className={`settings-segment-btn ${themePref === opt.value ? 'settings-segment-btn--active' : ''}`}
              onClick={() => handleThemeChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {saving.theme && <p className="settings-save-status">Saving…</p>}
        {saveError.theme && <p className="settings-save-error">{saveError.theme}</p>}

        <div className="settings-field-label" style={{ marginTop: '1rem' }}>Date format</div>
        <div className="settings-segment">
          {[
            { value: 'auto', label: 'Browser default' },
            { value: 'mdy',  label: 'Jun 15, 2024' },
            { value: 'dmy',  label: '15 Jun 2024' },
            { value: 'iso',  label: '2024-06-15' },
          ].map(opt => (
            <button
              key={opt.value}
              className={`settings-segment-btn ${dateFmt === opt.value ? 'settings-segment-btn--active' : ''}`}
              onClick={() => handleDateFormatChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {saving.date_format && <p className="settings-save-status">Saving…</p>}
        {saveError.date_format && <p className="settings-save-error">{saveError.date_format}</p>}

        <Link to="/settings/notifications" className="settings-link-row settings-link-row--top-sep">
          <div>
            <span className="settings-link-title">Notifications</span>
            <span className="settings-link-desc">Daily task reminders and observation alerts</span>
          </div>
          <span className="settings-link-action">Manage</span>
        </Link>
      </SectionCard>

      {/* ── Integrations ────────────────────────────────────────────────── */}
      <SectionCard title="Integrations">
        <ToggleRow
          label="PlantNet Species ID"
          description="AI-powered plant identification from photos"
          checked={plantnetSpecies}
          onChange={handlePlantnetSpeciesChange}
        />
        {saving.plantnet_species_enabled && <p className="settings-save-status">Saving…</p>}
        {saveError.plantnet_species_enabled && <p className="settings-save-error">{saveError.plantnet_species_enabled}</p>}

        {/* ============================================================
            FEATURE DISABLED: PlantNet health check toggle
            Disabled 2026-05 — diseases model only covers crops, not aroids.
            Re-enable alongside the backend endpoint and PlantDetail.js button.
            ============================================================
        <ToggleRow
          label="PlantNet Health Check"
          description="Disease and pest detection from photos"
          checked={plantnetDisease}
          onChange={handlePlantnetDiseaseChange}
        />
        {saving.plantnet_disease_enabled && <p className="settings-save-status">Saving…</p>}
        {saveError.plantnet_disease_enabled && <p className="settings-save-error">{saveError.plantnet_disease_enabled}</p>}
        */}
      </SectionCard>

      {/* ── Data Management ─────────────────────────────────────────────── */}
      <SectionCard title="Data Management">
        <Link to="/settings/field-options" className="settings-link-row">
          <div>
            <span className="settings-link-title">Field Options</span>
            <span className="settings-link-desc">Locations, tags, light sources, growing methods, and substrates</span>
          </div>
          <span className="settings-link-action">Manage</span>
        </Link>
        <Link to="/settings/export" className="settings-link-row">
          <div>
            <span className="settings-link-title">Export Data</span>
            <span className="settings-link-desc">Download your plants, observations, and measurements as CSV files</span>
          </div>
          <span className="settings-link-action">Export</span>
        </Link>
      </SectionCard>

      {/* ── Getting Started ─────────────────────────────────────────────── */}
      <GettingStartedCard />

      {/* ── Help & Tips ─────────────────────────────────────────────────── */}
      <SectionCard title="Help & Tips">
        <button
          type="button"
          className="settings-link-row settings-link-row--button"
          onClick={() => {
            setResetTipsDone(false);
            setSaveError(s => ({ ...s, guidance_reset: null }));
            setConfirmResetTips(true);
          }}
          disabled={saving.guidance_reset}
        >
          <div>
            <span className="settings-link-title">Reset all tips</span>
            <span className="settings-link-desc">Replay guidance tips on future visits</span>
          </div>
          <span className="settings-link-action">
            {saving.guidance_reset ? 'Resetting…' : 'Reset'}
          </span>
        </button>
        {confirmResetTips && (
          <div className="inline-confirm" style={{ margin: '0.35rem 0 0' }}>
            <span className="inline-confirm-msg">Reset all tips so they appear again next time?</span>
            <div className="inline-confirm-actions">
              <button type="button" className="inline-confirm-no" onClick={() => setConfirmResetTips(false)}>Cancel</button>
              <button type="button" className="inline-confirm-yes" onClick={handleResetTips}>Reset tips</button>
            </div>
          </div>
        )}
        {resetTipsDone && <p className="settings-save-status">Tips reset. Guidance will replay on future visits.</p>}
        {saveError.guidance_reset && <p className="settings-save-error">{saveError.guidance_reset}</p>}
      </SectionCard>

      {/* ── Help ────────────────────────────────────────────────────────── */}
      <SectionCard title="Help">
        <a href="mailto:help@aroidex.com" className="settings-link-row">
          <div>
            <span className="settings-link-title">Contact Support</span>
            <span className="settings-link-desc">help@aroidex.com</span>
          </div>
          <span className="settings-link-action">Email</span>
        </a>
        <div className="settings-link-row settings-link-row--placeholder">
          <div>
            <span className="settings-link-title">Help & FAQ</span>
            <span className="settings-link-desc">Getting started guide and frequently asked questions</span>
          </div>
          <span className="settings-link-action settings-muted-action">Coming soon</span>
        </div>
      </SectionCard>

      {/* ── About ───────────────────────────────────────────────────────── */}
      <SectionCard title="About">
        <div className="settings-about">
          <div>
            <div className="settings-about-name">Aroidex</div>
            <div className="settings-about-meta">Personal Plant Database</div>
            <div className="settings-about-build">
              {process.env.REACT_APP_BUILD_HASH
                ? `${process.env.REACT_APP_BUILD_HASH} · ${process.env.REACT_APP_BUILD_DATE}`
                : 'dev'}
            </div>
          </div>
        </div>
      </SectionCard>

      <div style={{ height: '1.5rem' }} />
    </div>
  );
}

// =============================================================================
// -- Notification Settings ----------------------------------------------------
// =============================================================================

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

async function subscribeToPush(vapidPublicKey) {
  const sw = await navigator.serviceWorker.ready;
  const sub = await sw.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  const p256dh = btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh'))));
  const auth   = btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))));
  await fetch('/api/notifications/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: sub.endpoint, p256dh, auth, user_agent: navigator.userAgent }),
  });
  return sub;
}

function OverrideRow({ name, overrides, onChange }) {
  const key = name;
  const current = overrides[key];
  const hasOverride = current !== undefined && current !== '';
  const [draft, setDraft] = useState(current != null ? String(current) : '');

  function commit(val) {
    const num = parseInt(val, 10);
    if (!isNaN(num) && num > 0) {
      onChange({ ...overrides, [key]: num });
    }
  }

  function clear() {
    const next = { ...overrides };
    delete next[key];
    onChange(next);
    setDraft('');
  }

  return (
    <div className="notif-override-row">
      <span className="notif-override-name">{name.replace(/^(substrate|location):/, '')}</span>
      {hasOverride ? (
        <div className="notif-override-active">
          <input
            type="number"
            className="notif-override-input"
            value={draft}
            min={1}
            onChange={e => setDraft(e.target.value)}
            onBlur={e => commit(e.target.value)}
          />
          <span className="notif-override-unit">days</span>
          <button className="notif-override-clear" onClick={clear} title="Use default">✕</button>
        </div>
      ) : (
        <button
          className="notif-override-add"
          onClick={() => { setDraft('14'); onChange({ ...overrides, [key]: 14 }); }}
        >
          + Override
        </button>
      )}
    </div>
  );
}

export function NotificationSettings() {
  const navigate = useNavigate();
  const [prefs, setPrefs]           = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState({});
  const [error, setError]           = useState(null);
  const [pushStatus, setPushStatus] = useState('unknown');
  const [pushBusy, setPushBusy]     = useState(false);
  const [pushError, setPushError]   = useState(null);
  const [vapidKey, setVapidKey]     = useState(null);
  const [locations,          setLocations]          = useState([]);
  const [substrateGroups,    setSubstrateGroups]    = useState([]); // [{ method, keys: ['substrate:X', ...] }]
  const [substrateOpen,      setSubstrateOpen]      = useState(false);
  const [locationOpen,       setLocationOpen]       = useState(false);

  useEffect(() => {
    Promise.all([
      fetch('/api/notifications/settings').then(r => r.ok ? r.json() : {}),
      fetch('/api/notifications/vapid-key').then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/locations').then(r => r.ok ? r.json() : []).catch(() => []),
      fetch('/growing-methods').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([p, vk, locs, gm]) => {
      setPrefs(p);
      setVapidKey(vk?.public_key ?? null);
      setLocations(locs.map(l => `location:${l.name}`));
      const seen = new Set();
      const groups = gm
        .filter(m => m.substrates?.length > 0)
        .map(m => ({
          method: m.name,
          keys: m.substrates
            .map(s => `substrate:${s.name}`)
            .filter(k => { if (seen.has(k)) return false; seen.add(k); return true; }),
        }))
        .filter(g => g.keys.length > 0);
      setSubstrateGroups(groups);
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
    });

    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      setPushStatus('unsupported');
    } else {
      setPushStatus(Notification.permission);
    }
  }, []);

  // Auto-subscribe when vapidKey becomes available and permission is already granted.
  // This handles the case where the service worker was unregistered (killing the old
  // subscription) and the user revisits Settings — we silently re-register.
  useEffect(() => {
    if (!vapidKey || pushStatus !== 'granted') return;
    subscribeToPush(vapidKey).catch(() => {
      // Silently ignore — user can always use the manual button if this fails
    });
  }, [vapidKey, pushStatus]);

  async function savePref(key, value) {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const res = await fetch(`/api/notifications/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: String(value) }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setPrefs(p => ({ ...p, [key]: String(value) }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  }

  async function handleEnablePush() {
    if (!vapidKey) return;
    setPushBusy(true);
    setPushError(null);
    try {
      const permission = await Notification.requestPermission();
      setPushStatus(permission);
      if (permission === 'granted') {
        await subscribeToPush(vapidKey);
      }
    } catch (err) {
      setPushError(err.message);
    } finally {
      setPushBusy(false);
    }
  }

  const pref = (key, def = '') => prefs[key] ?? def;
  const isBool = key => pref(key, 'false') === 'true';

  const overrides = (() => {
    try { return JSON.parse(pref('obs_staleness_overrides', '{}')); }
    catch { return {}; }
  })();

  function saveOverrides(next) {
    savePref('obs_staleness_overrides', JSON.stringify(next));
  }

  if (loading) return <div className="app"><p style={{ padding: '1rem' }}>Loading…</p></div>;

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/settings', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Notifications</h1>
        </div>
      </header>

      <div className="settings-body">
        {error && <p className="settings-save-error" style={{ margin: '0 0 1rem' }}>{error}</p>}

        {/* ── Push permission status banner ──────────────────────────────── */}
        {pushStatus === 'unsupported' && (
          <div className="notif-banner notif-banner--warn">
            Your browser does not support push notifications. Notifications will only appear while the app is open.
          </div>
        )}
        {pushStatus === 'default' && vapidKey && (
          <div className="notif-banner">
            <span>Enable browser notifications to receive alerts in the background.</span>
            <button className="btn btn-primary btn-sm" onClick={handleEnablePush} disabled={pushBusy}>
              {pushBusy ? 'Enabling…' : 'Enable Notifications'}
            </button>
            {pushError && <span className="settings-save-error">{pushError}</span>}
          </div>
        )}
        {pushStatus === 'denied' && (
          <div className="notif-banner notif-banner--warn">
            Notifications are blocked. Open your browser settings to allow notifications for this site, then reload.
          </div>
        )}
        {pushStatus === 'granted' && (
          <div className="notif-banner notif-banner--ok">
            Browser notifications are enabled.
          </div>
        )}

        {/* ── Care Plan ─────────────────────────────────────────────────── */}
        <SectionCard title="Care Plan">
          <ToggleRow
            label="Daily task reminders"
            description="Get notified about tasks scheduled for today"
            checked={isBool('care_plan_notif_enabled')}
            onChange={val => savePref('care_plan_notif_enabled', val)}
          />
          {saving.care_plan_notif_enabled && <p className="settings-save-status">Saving…</p>}
          {isBool('care_plan_notif_enabled') && (
            <div className="notif-time-row">
              <label className="notif-time-label">Send at</label>
              <input
                type="time"
                className="notif-time-input"
                value={pref('care_plan_notif_time', '08:00')}
                onChange={e => savePref('care_plan_notif_time', e.target.value)}
              />
              <span className="notif-time-hint">
                triggers the next time you open the app after this time
              </span>
            </div>
          )}
        </SectionCard>

        {/* ── Plant Observations ────────────────────────────────────────── */}
        <SectionCard title="Plant Observations">
          <ToggleRow
            label="Care Round reminders"
            description="Start an Overdue Care Round when plants haven't been observed recently"
            checked={isBool('obs_staleness_enabled')}
            onChange={val => savePref('obs_staleness_enabled', val)}
          />
          {saving.obs_staleness_enabled && <p className="settings-save-status">Saving…</p>}
          {isBool('obs_staleness_enabled') && (
            <div className="notif-threshold-section">
              <div className="notif-threshold-row">
                <label className="notif-time-label">Alert after</label>
                <input
                  type="number"
                  className="notif-threshold-input"
                  min={1}
                  max={365}
                  inputMode="numeric"
                  value={pref('obs_staleness_days', '14')}
                  onChange={e => savePref('obs_staleness_days', e.target.value)}
                />
                <span className="notif-time-hint">days without an observation before triggering an Overdue Care Round</span>
              </div>

              {(substrateGroups.length > 0 || locations.length > 0) && (
                <p className="notif-overrides-intro">
                  By default, all plants use the same threshold above. You can set a different number of days for specific substrates or locations — useful when some plants naturally need water less often (e.g. semi-hydro) or live in a lower-light spot.
                </p>
              )}

              {substrateGroups.length > 0 && (
                <div className="notif-overrides-section">
                  <button
                    className="notif-overrides-toggle"
                    onClick={() => setSubstrateOpen(o => !o)}
                    aria-expanded={substrateOpen}
                  >
                    <span>Custom thresholds by substrate</span>
                    <Icon name="chevronDown" size={14} className={`notif-overrides-chevron${substrateOpen ? ' notif-overrides-chevron--open' : ''}`} />
                  </button>
                  {substrateOpen && (
                    <div className="notif-overrides-body">
                      {substrateGroups.map(group => (
                        <div key={group.method} className="notif-overrides-group">
                          <div className="notif-overrides-group-label">{group.method}</div>
                          {group.keys.map(key => (
                            <OverrideRow key={key} name={key} overrides={overrides} onChange={saveOverrides} />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {locations.length > 0 && (
                <div className="notif-overrides-section">
                  <button
                    className="notif-overrides-toggle"
                    onClick={() => setLocationOpen(o => !o)}
                    aria-expanded={locationOpen}
                  >
                    <span>Custom thresholds by location</span>
                    <Icon name="chevronDown" size={14} className={`notif-overrides-chevron${locationOpen ? ' notif-overrides-chevron--open' : ''}`} />
                  </button>
                  {locationOpen && (
                    <div className="notif-overrides-body">
                      {locations.map(key => (
                        <OverrideRow key={key} name={key} overrides={overrides} onChange={saveOverrides} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </SectionCard>

        <div style={{ height: '1.5rem' }} />
      </div>
    </div>
  );
}
